import React, { useEffect, useState, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import { trans } from '../../../../_providers/Translation'
import '../../../../assets/css/partials/status-diagram.css'
import DateRange from '@/components/partials/DateRange'
import moment from 'moment'
import { Button } from '@mui/material'
import CompactFilterLayout from '@/components/partials/CompactFilterLayout'
import filterImage from '@/assets/img/icons/filter.svg'
import { useHistory } from 'react-router-dom'

export default function ManualOverview({
    endpoint = 'dashboard/manuals/overview',
    title = 'Ръководства',
    colors = ['#5563FF', '#C084FC', '#374151'],
    donut = true,
    size = 220
}) {
    const history = useHistory()

    const [state, setState] = useState({ loading: true, data: null })
    const [filters, setFilters] = useState({
        date_from: null,
        date_to: null,
    })

    const compactFilterLayoutRef = useRef(null)
    const fetchDebounceRef = useRef(null)

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            setState(prev => ({ ...prev, loading: true }))
            Api.get(`${endpoint}?date_from=${filters.date_from || ''}&date_to=${filters.date_to || ''}`)
                .then(res => {
                    const payload = res?.data ?? res
                    setState({ loading: false, data: payload })
                })
                .catch(() => setState({ loading: false, data: null }))
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [endpoint, filters])

    function handleFilterChange(name, value) {
        setFilters(prev => ({ ...prev, [name]: value }))
    }

    function getAppliedFiltersCount() {
        return filters.date_from || filters.date_to ? 1 : 0
    }

    // Convert API data format to items array
    const apiData = state.data || {}
    const sampleData = {
        manuals: 2,
        system_errors: 1,
        problems: 0
    }

    const dataSource = Object.keys(apiData).length > 0 ? apiData : sampleData

    // Convert object to items array
    const itemsSource = [
        { key: 'manuals', label: trans('nav.manuals'), value: dataSource.manuals || 0, color1: "#181F67", color2: "#5563FF", param: "?page=1&tab=manuals" },
        { key: 'system_errors', label: trans('tabs.errors'), value: dataSource.system_errors || 0, color1: "#fd7e9d", color2: "#f3a7ba", param: "?page=1&tab=errors" },
        { key: 'problems', label: trans('random.commonProblemsShort'), value: dataSource.problems || 0, color1: "#2D467C", color2: "#777f92", param: "?page=1&tab=problems" }
    ]

    const items = itemsSource

    function getTotalResults() {
        return items.reduce((sum, it) => sum + Number(it.value || 0), 0)
    }

    return (
        <div className="manuals-chart">
            <div className='head'>
                <h3>{title}</h3>
                <div className="right">
                    <Button onClick={(e) => {
                        compactFilterLayoutRef.current.open(e)
                    }}>
                        <img src={filterImage} alt="Filter" />
                        {getAppliedFiltersCount() ?
                            <i>{getAppliedFiltersCount()}</i>
                            : <></>}
                    </Button>
                    <CompactFilterLayout
                        ref={compactFilterLayoutRef}
                        appliedFiltersCount={getAppliedFiltersCount()}
                        onClearClick={() => setFilters({ date_from: null, date_to: null })}
                    >
                        <div className='row'>
                            <div className='col'>
                                <DateRange
                                    startDate={filters.date_from || ''}
                                    endDate={filters.date_to || ''}
                                    onStartDateChange={(date) => handleFilterChange('date_from', date)}
                                    onEndDateChange={(date) => handleFilterChange('date_to', date)}
                                />
                            </div>
                        </div>
                    </CompactFilterLayout>
                </div>
            </div>
            <hr />
            {state.loading ?
                <Skeleton height={200} count={1} />
                :
                getTotalResults() === 0 ?
                    <div
                        className="alert warning"
                        style={{
                            marginBottom: 0
                        }}
                    >
                        {trans("messages.noDataFound")}
                    </div>
                    :
                    <div className="body">
                        <div className="bubble-chart">
                            {(() => {
                                const maxSize = 200
                                const minSize = 70
                                const sorted = [...items].sort((a, b) => Number(b.value) - Number(a.value))
                                const maxVal = Math.max(...sorted.map(i => Number(i.value)), 1)

                                const bubbles = sorted.map(it => {
                                    const ratio = Number(it.value) / maxVal
                                    const diameter = minSize + (maxSize - minSize) * Math.sqrt(ratio)
                                    return { ...it, diameter }
                                })

                                // Positions: largest left, medium top-right, smallest bottom-right
                                const positions = [
                                    { top: '10%', left: '0' },
                                    { top: '0', right: '0' },
                                    { bottom: '0', right: '5%' },
                                ]

                                return bubbles.map((b, idx) => (
                                    <div
                                        key={b.key}
                                        className="bubble"
                                        style={{
                                            width: b.diameter,
                                            height: b.diameter,
                                            backgroundImage: `linear-gradient(to bottom, ${b.color1}, ${b.color2})`,
                                            border: "4px solid #fff",
                                            cursor: 'pointer',
                                            ...positions[idx],
                                        }}
                                        onClick={() => {
                                            history.push(`/schedule/manuals${b.param}`)
                                        }}
                                    >
                                        {b.value}
                                    </div>
                                ))
                            })()}
                        </div>

                        <ul>
                            {items.map((it, idx) => {
                                return (
                                    <li
                                        key={it.key || it.label || idx}
                                    >
                                        <span style={{ backgroundImage: `linear-gradient(to bottom, ${it.color1}, ${it.color2})` }} />
                                        {it.label}
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
            }
        </div>
    )
}